<template>
  <div 
    class="image-container"
    :class="{ 
      'is-loading': loading,
      'has-error': error,
      [`aspect-ratio-${aspectRatio}`]: aspectRatio
    }"
    :style="containerStyle"
  >
    <img
      v-if="!error"
      :src="resolvedSrc"
      :alt="alt"
      :width="width"
      :height="height"
      @load="handleLoad"
      @error="handleError"
      :class="{ 'fade-in': !loading }"
      loading="lazy"
    />
    <div v-if="loading" class="loading-placeholder" :style="placeholderStyle">
      <span class="loading-spinner"></span>
    </div>
    <div v-if="error" class="error-placeholder" :style="placeholderStyle">
      <span class="error-icon">!</span>
      <span class="error-text">{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseImage',
  props: {
    src: {
      type: String,
      required: true
    },
    alt: {
      type: String,
      required: true
    },
    width: {
      type: [Number, String],
      default: null
    },
    height: {
      type: [Number, String],
      default: null
    },
    aspectRatio: {
      type: String,
      default: null,
      validator: (value) => ['square', '16/9', '4/3', '3/2'].includes(value)
    },
    fit: {
      type: String,
      default: 'contain',
      validator: (value) => ['cover', 'contain', 'fill'].includes(value)
    },
    errorMessage: {
      type: String,
      default: 'Failed to load image'
    }
  },
  data() {
    return {
      loading: true,
      error: false,
      imageDimensions: {
        width: null,
        height: null
      }
    }
  },
  computed: {
    resolvedSrc() {
      try {
        // For images in the assets directory
        return `/assets/images/${this.src}`;
      } catch (e) {
        // For external images or absolute paths
        return this.src;
      }
    },
    containerStyle() {
      const style = {}
      
      if (this.width) {
        style.width = typeof this.width === 'number' ? `${this.width}px` : this.width
      }
      if (this.height) {
        style.height = typeof this.height === 'number' ? `${this.height}px` : this.height
      }
      
      return style
    },
    placeholderStyle() {
      const style = {}
      
      if (this.width) {
        style.width = typeof this.width === 'number' ? `${this.width}px` : this.width
      }
      if (this.height) {
        style.height = typeof this.height === 'number' ? `${this.height}px` : this.height
      }
      
      return style
    }
  },
  methods: {
    handleLoad(event) {
      // Store the actual image dimensions
      this.imageDimensions = {
        width: event.target.naturalWidth,
        height: event.target.naturalHeight
      }
      
      this.loading = false
      this.error = false
      this.$emit('load')
    },
    handleError() {
      this.loading = false
      this.error = true
      this.$emit('error')
    }
  }
}
</script>

<style lang="scss">
@use '@/assets/scss/main.scss';

.image-container {
  position: relative;
  overflow: hidden;
  
  border-radius: 4px;
  min-height: 100px; // Minimum height to prevent extreme layout shifts

  img {
    width: 100%;
    height: 100%;
    object-fit: var(--image-fit, contain);
    opacity: 0;
    transition: opacity 0.3s ease;
    display: block; // Prevents extra space below image

    &.fade-in {
      opacity: 1;
    }
  }

  &.aspect-ratio-square {
    aspect-ratio: 1;
  }

  &.aspect-ratio-16\/9 {
    aspect-ratio: 16/9;
  }

  &.aspect-ratio-4\/3 {
    aspect-ratio: 4/3;
  }

  &.aspect-ratio-3\/2 {
    aspect-ratio: 3/2;
  }
}

.loading-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;

  .loading-spinner {
    width: 24px;
    height: 24px;
    border: 2px solid var(--primary-color);
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
}

.error-placeholder {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  color: #dc3545;
  padding: 1rem;
  text-align: center;

  .error-icon {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 0.5rem;
  }

  .error-text {
    font-size: 14px;
    color: #666;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style> 